import {useContext} from 'react';
import {CourseContext} from 'contexts/CourseContext';
import type {CourseId} from 'types/common';
import {isNullOrUndefined} from 'utils/general';
import {getParentCourseId} from './apputils';

/**
 * Whether `courseId` is one of the courses whose applicants historically
 * completed application tasks in a parent course before the course document
 * carried `parentCourseId`. Kept only as the fallback in
 * {@link resolveParentCourseId}; retire it once those course documents are
 * backfilled (tracked in
 * [#3570](https://github.com/CodeinPlace/cip/issues/3570)).
 */
const hasLegacyParentCourse = (courseId: CourseId): boolean =>
  courseId.startsWith('pylatino') || courseId.startsWith('cip');

/**
 * Resolves the course whose per-user progress documents also count toward
 * completion in `courseId`. Application tasks (lessons, assignments) are
 * completed under the parent course, so an enrolled student's course page
 * overlays that progress onto its own.
 *
 * Field-first: the course document's `parentCourseId` wins when it names a
 * different course. Otherwise the legacy prefix rule decides whether the
 * hardcoded {@link getParentCourseId} applies.
 *
 * @param args.courseId - The course whose completion is being displayed.
 * @param args.parentCourseId - The `parentCourseId` field from the course
 *   document. When omitted (field absent, or the document has not loaded), the
 *   legacy prefix rule is the only source.
 * @return The parent course id, or `null` when no other course's progress
 *   counts toward `courseId`.
 */
export function resolveParentCourseId({
  courseId,
  parentCourseId,
}: {
  courseId: CourseId;
  parentCourseId?: CourseId;
}): CourseId | null {
  if (
    !isNullOrUndefined(parentCourseId) &&
    parentCourseId !== '' &&
    parentCourseId !== courseId
  ) {
    return parentCourseId;
  }
  if (hasLegacyParentCourse(courseId)) {
    return getParentCourseId(courseId);
  }
  return null;
}

/**
 * {@link resolveParentCourseId} over the ambient {@link CourseContext}.
 *
 * @param courseId - The course whose completion is being displayed. The
 *   course document field is only trusted when the ambient context is loaded
 *   for this same course; otherwise the legacy rule alone decides.
 * @return The parent course id (or `null`), plus whether the course document
 *   is still loading. While loading, the result may still change.
 */
export function useParentCourseId(courseId: CourseId): {
  parentCourseId: CourseId | null;
  isLoading: boolean;
} {
  const course = useContext(CourseContext);
  const isSameCourse = course.courseId === courseId;
  const isLoading = isSameCourse && course.isLoading;
  return {
    parentCourseId: resolveParentCourseId({
      courseId,
      parentCourseId: isSameCourse ? course.parentCourseId : undefined,
    }),
    isLoading,
  };
}

/**
 * Overlays a parent course's progress onto a course's own progress. A key is
 * complete when it is complete in either map. Both maps hold only booleans
 * (see `UserAssignmentProgressMap` and `UserLessonsProgressMap`).
 *
 * @param own - Progress recorded in the course itself. `null`/`undefined`
 *   means the document does not exist or has not loaded.
 * @param parent - Progress recorded in the parent course. `null`/`undefined`
 *   means there is no parent, or its document does not exist or has not
 *   loaded.
 * @return The merged map, or `null` when neither map is available so callers
 *   can keep distinguishing "nothing loaded" from "nothing completed".
 */
export function mergeProgressMaps(
  own: Record<string, boolean> | null | undefined,
  parent: Record<string, boolean> | null | undefined,
): Record<string, boolean> | null {
  if (isNullOrUndefined(own) && isNullOrUndefined(parent)) {
    return null;
  }
  if (isNullOrUndefined(parent)) {
    return own;
  }
  if (isNullOrUndefined(own)) {
    return parent;
  }
  const merged: Record<string, boolean> = {...own};
  for (const key of Object.keys(parent)) {
    if (parent[key] && !merged[key]) {
      merged[key] = true;
    }
  }
  return merged;
}
